import type { Flashcard, FlashcardDeck, FlashcardMedia, FlashcardNote, FlashcardNoteType, FlashcardTemplate } from "@japa-reader/contracts";

export type RenderedFlashcard = {
  html: string;
  plainText: string;
  typeAnswerField: string | null;
  audioCues: FlashcardAudioCue[];
};

export type FlashcardAudioCue =
  | { kind: "tts"; text: string; language: string; voices: string[]; speed: number }
  | { kind: "media"; source: string };

export function renderFlashcardSide(input: {
  card: Flashcard;
  note: FlashcardNote;
  noteType: FlashcardNoteType;
  deck?: FlashcardDeck;
  media?: FlashcardMedia[];
  side: "front" | "back";
}): RenderedFlashcard {
  const template = input.noteType.templates.find((candidate) => candidate.id === input.card.templateId)
    ?? input.noteType.templates[input.card.templateOrdinal]
    ?? input.noteType.templates[0];
  if (!template) return { html: "", plainText: "", typeAnswerField: null, audioCues: [] };
  const front = renderTemplate(template.frontTemplate, input, template, "front", "");
  const rendered = input.side === "front"
    ? front
    : renderTemplate(template.backTemplate, input, template, "back", front.html);
  const html = sanitizeCardHtml(rendered.html);
  return { html, plainText: htmlToText(html), typeAnswerField: rendered.typeAnswerField, audioCues: rendered.audioCues };
}

export function cardTemplateCss(noteType: FlashcardNoteType, card: Flashcard) {
  const template = noteType.templates.find((candidate) => candidate.id === card.templateId)
    ?? noteType.templates[card.templateOrdinal]
    ?? noteType.templates[0];
  return sanitizeTemplateCss(template?.css || "");
}

export function fieldPlainText(note: FlashcardNote, fieldName: string) {
  return htmlToText(note.fields[fieldName] || "");
}

function renderTemplate(
  source: string,
  input: { card: Flashcard; note: FlashcardNote; noteType: FlashcardNoteType; deck?: FlashcardDeck; media?: FlashcardMedia[] },
  template: FlashcardTemplate,
  side: "front" | "back",
  frontSide: string,
) {
  let html = source;
  let typeAnswerField: string | null = null;
  const audioCues: FlashcardAudioCue[] = [];
  const values: Record<string, string> = {
    ...input.note.fields,
    Tags: input.note.tags.join(" "),
    Type: input.noteType.name,
    Deck: input.deck?.name || "",
    Subdeck: input.deck?.name.split("::").at(-1) || input.deck?.name || "",
    Card: template.name,
    CardFlag: String(input.card.flag || ""),
    FrontSide: frontSide,
  };

  for (let pass = 0; pass < 10; pass += 1) {
    const before = html;
    html = html.replace(/\{\{#([^}]+)\}\}([\s\S]*?)\{\{\/\1\}\}/g, (_match, field: string, contents: string) => (
      htmlToText(values[field.trim()] || "").trim() ? contents : ""
    ));
    html = html.replace(/\{\{\^([^}]+)\}\}([\s\S]*?)\{\{\/\1\}\}/g, (_match, field: string, contents: string) => (
      htmlToText(values[field.trim()] || "").trim() ? "" : contents
    ));
    if (html === before) break;
  }

  html = html.replace(/\{\{cloze:([^}]+)\}\}/gi, (_match, field: string) => renderCloze(
    values[field.trim()] || "",
    input.card.clozeOrdinal ?? 1,
    side,
  ));
  html = html.replace(/\{\{tts\s+([^:}]+):([^}]+)\}\}/gi, (_match, rawOptions: string, rawField: string) => {
    const [language = "", ...settings] = rawOptions.trim().split(/\s+/);
    const field = rawField.trim();
    const rawValue = field.toLowerCase().startsWith("cloze:")
      ? renderCloze(values[field.slice(6).trim()] || "", input.card.clozeOrdinal ?? 1, side)
      : values[field] || "";
    const text = htmlToText(rawValue).trim();
    const voices = settings.find((setting) => setting.startsWith("voices="))?.slice(7).split(",").filter(Boolean) || [];
    const speed = Number(settings.find((setting) => setting.startsWith("speed="))?.slice(6) || "1");
    if (text) audioCues.push({ kind: "tts", text, language: language.replace("_", "-"), voices, speed: Number.isFinite(speed) ? Math.min(4, Math.max(0.1, speed)) : 1 });
    return escapeHtml(text);
  });
  html = html.replace(/\{\{type:([^}]+)\}\}/gi, (_match, field: string) => {
    typeAnswerField = field.trim();
    return side === "front" ? '<span class="type-answer-slot" aria-hidden="true"></span>' : escapeHtml(htmlToText(values[field.trim()] || ""));
  });
  html = html.replace(/\{\{hint:([^}]+)\}\}/gi, (_match, field: string) => {
    const value = values[field.trim()] || "";
    return value ? `<details class="card-hint"><summary>Mostrar dica</summary>${value}</details>` : "";
  });
  html = html.replace(/\{\{text:([^}]+)\}\}/gi, (_match, field: string) => escapeHtml(htmlToText(values[field.trim()] || "")));
  html = html.replace(/\{\{([^}:]+)\}\}/g, (_match, field: string) => values[field.trim()] ?? "");
  html = html.replace(/\[sound:([^\]]+)\]/gi, (_match, source: string) => {
    const normalized = source.trim();
    const resolved = resolveMediaSource(normalized, input.media);
    if (resolved) audioCues.push({ kind: "media", source: resolved });
    return `<span class="card-sound" data-sound-source="${escapeHtml(resolved || normalized)}">🔊</span>`;
  });
  html = resolveEmbeddedMedia(html, input.media);
  return { html, typeAnswerField, audioCues };
}

function resolveEmbeddedMedia(html: string, media: FlashcardMedia[] | undefined) {
  return html.replace(/\b(src|poster|href)=(['"])([^'"]+)\2/gi, (match, attribute: string, quote: string, source: string) => {
    const resolved = resolveMediaSource(source.trim(), media);
    return resolved ? `${attribute}=${quote}${escapeHtml(resolved)}${quote}` : match;
  });
}

function resolveMediaSource(source: string, media: FlashcardMedia[] | undefined) {
  if (!source) return "";
  const reference = source.toLowerCase().startsWith("media:") ? source.slice(6) : source;
  const item = media?.find((candidate) => candidate.id === reference || candidate.name === reference);
  if (item) return item.url || "";
  return source.toLowerCase().startsWith("media:") ? "" : source;
}

function renderCloze(value: string, ordinal: number, side: "front" | "back") {
  return value.replace(/\{\{c(\d+)::([\s\S]*?)(?:::(.*?))?\}\}/gi, (_match, rawOrdinal: string, answer: string, hint?: string) => {
    const current = Number(rawOrdinal) === ordinal;
    if (!current) return answer;
    if (side === "back") return `<mark class="cloze-answer">${answer}</mark>`;
    return `<span class="cloze-question">[${hint?.trim() || "…"}]</span>`;
  });
}

function sanitizeCardHtml(html: string) {
  if (typeof DOMParser === "undefined") return html.replace(/<script[\s\S]*?<\/script>/gi, "");
  const document = new DOMParser().parseFromString(`<div id="flashcard-root">${html}</div>`, "text/html");
  const root = document.getElementById("flashcard-root");
  if (!root) return "";
  root.querySelectorAll("script,iframe,object,embed,form,meta,base").forEach((element) => element.remove());
  root.querySelectorAll("*").forEach((element) => {
    for (const attribute of [...element.attributes]) {
      const name = attribute.name.toLowerCase();
      const value = attribute.value.trim().toLowerCase();
      if (name.startsWith("on") || name === "srcdoc" || ((name === "href" || name === "src") && value.startsWith("javascript:"))) {
        element.removeAttribute(attribute.name);
      }
    }
  });
  return root.innerHTML;
}

function sanitizeTemplateCss(css: string) {
  return css
    .replace(/@import[\s\S]*?;/gi, "")
    .replace(/url\(\s*['"]?javascript:[^)]+\)/gi, "")
    .slice(0, 50_000);
}

function htmlToText(html: string) {
  if (typeof DOMParser !== "undefined") {
    return new DOMParser().parseFromString(html, "text/html").body.textContent || "";
  }
  return html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}

function escapeHtml(value: string) {
  return value.replace(/[&<>"']/g, (character) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#039;",
  })[character] || character);
}
